import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { updateSEOMeta, resetSEOMeta, useBlogLanguage } from '../utils/seoUtils';

const WHATSAPP_LINK = "https://wa.me/529841585732?text=Hi%2C%20I%20want%20to%20schedule%20a%20free%20consultation%20with%20Marea%20Riviera";

export default function BlogInvestingTulumForeignBuyers() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const es = language === 'es';
  useBlogLanguage('en');

  useEffect(() => {
    updateSEOMeta({
      title: es ? 'Invertir en Tulum 2026: Guía Completa para Compradores Extranjeros | Marea Riviera' : 'Investing in Tulum Real Estate 2026: Complete Guide for Foreign Buyers | Marea Riviera',
      description: es ? '¿Pueden los extranjeros comprar propiedad en Tulum? Sí. Guía completa 2026: fideicomiso, costos de cierre, mejores zonas, ROI y proceso paso a paso.' : "Can foreigners buy property in Tulum? Yes. Here's the complete 2026 guide: fideicomiso, closing costs, best zones, ROI, and step-by-step buying process.",
      url: 'https://mareariviera.com/blog/investing-tulum-real-estate-foreign-buyers',
      lang: es ? 'es' : 'en'
    });
    window.scrollTo(0, 0);
    return () => { resetSEOMeta(); };
  }, [es]);

  const scrollToContact = () => { sessionStorage.setItem('scrollTo', 'contact'); navigate('/'); };

  return (
    <div className="bg-[#FAFAF7] min-h-screen pt-[120px]">
      <div className="bg-[#0A2235] py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-[#2BBFBF] hover:text-[#7ECFCF] transition-colors mb-8 font-inter text-sm" data-testid="blog-back-btn">
            <ArrowLeft className="w-4 h-4" /> {es ? 'Volver al inicio' : 'Back to home'}
          </button>
          <div className="flex items-center gap-4 text-[#FAFAF7]/50 font-inter text-sm mb-6">
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {es ? '29 de mayo, 2026' : 'May 29, 2026'}</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> 14 min read</span>
          </div>
          <h1 className="font-playfair text-3xl sm:text-4xl lg:text-5xl text-[#FAFAF7] leading-tight mb-6" data-testid="blog-title">
            {es ? 'Invertir en Tulum: Guía Completa para Compradores Extranjeros 2026' : 'Investing in Tulum Real Estate: Complete Guide for Foreign Buyers 2026'}
          </h1>
          <p className="font-inter text-[#FAFAF7]/70 text-lg leading-relaxed">
            {es ? '¿Pueden los extranjeros comprar propiedad en Tulum? Sí. Guía completa: fideicomiso, costos de cierre, mejores zonas, ROI y proceso paso a paso.' : "Can foreigners buy property in Tulum? Yes. Here's the complete guide: fideicomiso, closing costs, best zones, ROI, and step-by-step buying process."}
          </p>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="blog-content">
        <div className="prose-marea">
          <p>Tulum is one of the most sought-after real estate markets in Latin America — and increasingly, buyers from the United States, Canada, Europe, and beyond are asking the same question: <strong>Can I legally buy property in Tulum as a foreigner?</strong></p>
          <p>The answer is yes. But the process is different from what most international buyers are used to. This guide walks you through everything you need to know — from the legal structure to the step-by-step buying process, closing costs, and how to protect your investment.</p>

          <h2>Can Foreigners Own Property in Tulum?</h2>
          <p>Yes — with one important caveat.</p>
          <p>Tulum is located within Mexico's <strong>Restricted Zone</strong>: a 50 km band along the coastline where foreign nationals cannot hold land directly in their name. This is established in Article 27 of Mexico's Constitution.</p>
          <p>However, foreigners can — and do — buy property in Tulum every day through a legal structure called a <strong>fideicomiso</strong> (bank trust). This is the standard path for international buyers in all of Mexico's coastal destinations, including Tulum, Playa del Carmen, Cancún, Puerto Vallarta, and Los Cabos.</p>

          <h2>What Is a Fideicomiso?</h2>
          <p>A fideicomiso is a real estate trust established with a Mexican bank. Here's how it works:</p>
          <ul>
            <li>The <strong>bank holds the legal title</strong> to the property on your behalf</li>
            <li><strong>You are the sole beneficiary</strong> and retain full ownership rights</li>
            <li>You can <strong>use, rent, sell, renovate, or pass on</strong> the property freely</li>
            <li>The trust is valid for <strong>50 years and renewable indefinitely</strong></li>
            <li>You can <strong>name heirs</strong> — bypassing Mexican probate entirely</li>
          </ul>
          <p>Think of it as a shell: the bank's name is on the deed, but you have complete control and all the economic benefits of ownership.</p>

          <h3>Fideicomiso Costs (2026)</h3>
          <div className="overflow-x-auto my-8">
            <table>
              <thead><tr><th>Cost</th><th>Amount</th></tr></thead>
              <tbody>
                <tr><td>Setup fee</td><td>$2,000 – $5,000 USD</td></tr>
                <tr><td>Annual maintenance fee</td><td>$500 – $1,000 USD/year</td></tr>
                <tr><td>SRE permit (government approval)</td><td>Included in notary process</td></tr>
              </tbody>
            </table>
          </div>
          <p>The fideicomiso setup is a standard part of the closing process, handled by your notary (<em>notario público</em>) and the bank. You don't need to manage it yourself.</p>

          <h2>Total Closing Costs for Foreign Buyers</h2>
          <p>One of the most common surprises for international buyers: <strong>closing costs in Mexico are higher than in the US or Canada</strong>. Budget for 6%–9% of the purchase price on top of the property price.</p>
          <div className="overflow-x-auto my-8">
            <table>
              <thead><tr><th>Cost</th><th>Estimated Amount</th></tr></thead>
              <tbody>
                <tr><td>State acquisition tax (ISAI)</td><td>2%–4% of purchase price</td></tr>
                <tr><td>Notary fees</td><td>1%–2% of purchase price</td></tr>
                <tr><td>Property registry</td><td>~0.5%–1%</td></tr>
                <tr><td>Fideicomiso setup</td><td>$2,000–$5,000 USD</td></tr>
                <tr><td>Appraisal fee</td><td>$300–$600 USD</td></tr>
                <tr><td><strong>Total estimated closing costs</strong></td><td><strong>6%–9% of purchase price</strong></td></tr>
              </tbody>
            </table>
          </div>
          <p><em>Example: on a $200,000 USD property, budget $12,000–$18,000 USD in closing costs.</em></p>

          <h2>Step-by-Step: How to Buy Property in Tulum as a Foreigner</h2>

          <h3>Step 1 — Choose your property and sign the purchase agreement</h3>
          <p>Work with a licensed broker who knows the local market. Once you've selected a property, you'll sign a <em>promesa de compraventa</em> (purchase agreement) and pay a deposit — typically 10%–30% of the purchase price.</p>

          <h3>Step 2 — Hire a notario público</h3>
          <p>In Mexico, all real estate transactions must go through a <em>notario público</em> — a government-appointed legal officer who formalizes the sale, calculates taxes, and registers the deed. Your broker can recommend a reputable one.</p>

          <h3>Step 3 — Fideicomiso application</h3>
          <p>Your notary submits the fideicomiso application to a Mexican bank (BBVA, Santander, HSBC, and others offer this service). You'll need:</p>
          <ul>
            <li>Valid passport</li>
            <li>Proof of address (from your home country or Mexico)</li>
            <li>Purchase agreement</li>
            <li>Property appraisal</li>
          </ul>
          <p>This process takes approximately <strong>4–6 weeks</strong>.</p>

          <h3>Step 4 — Due diligence</h3>
          <p>Before closing, verify:</p>
          <ul>
            <li><strong>Clean title</strong> — no liens, encumbrances, or disputes</li>
            <li><strong>MIA vigente</strong> (Environmental Impact Authorization) — mandatory for all developments</li>
            <li><strong>No ejido origins</strong> — agricultural communal land that cannot be legally transferred is the #1 trap for foreign buyers in Mexico</li>
            <li><strong>HOA rules</strong> — some buildings restrict short-term rentals (important if you plan to list on Airbnb)</li>
          </ul>

          <h3>Step 5 — Closing</h3>
          <p>The notario formalizes the transaction, registers the deed, and transfers the fideicomiso to your name. Total closing timeline: <strong>30–60 days</strong> for resale properties, or the construction timeline for pre-sale condos.</p>

          <h3>Step 6 — Post-closing: RETUR-Q registration</h3>
          <p>Since late 2025, all short-term rental operators in Quintana Roo must register with the state tourism registry (RETUR-Q). If you plan to rent on Airbnb or Vrbo, factor in this registration. Fines for non-compliance reach up to 100,000 MXN.</p>

          <h2>Best Zones for Foreign Buyers in Tulum</h2>
          <p>Not all of Tulum is equal for investment purposes. Here's a quick breakdown:</p>

          <h3>Aldea Zamá</h3>
          <p>The most consolidated and internationally recognized neighborhood in Tulum. Full infrastructure, high-end amenities, strong rental demand year-round. Most popular with US and Canadian buyers. Higher entry prices, but also the strongest ROI track record.</p>

          <h3>La Veleta</h3>
          <p>Growing fast, with more accessible prices than Aldea Zamá. Strong appreciation potential as infrastructure develops. Good option for buyers looking to maximize long-term capital gains.</p>

          <h3>Tulum Centro / Pueblo</h3>
          <p>Lower price point, authentic local atmosphere. Popular with digital nomads and budget-conscious travelers. Good occupancy rates but lower nightly rates.</p>

          <h3>Coastal Hotel Zone</h3>
          <p>Highest nightly rates in Tulum ($300–$650 USD/night for premium properties). Strictest land use regulations and highest purchase prices. Best for buyers targeting the luxury vacation rental segment.</p>

          <h2>What Returns Can Foreign Buyers Expect?</h2>
          <p>Based on 2026 market data:</p>
          <div className="overflow-x-auto my-8">
            <table>
              <thead><tr><th>Property Type</th><th>Expected Net ROI</th></tr></thead>
              <tbody>
                <tr><td>Average market property</td><td>5%–8% net annually</td></tr>
                <tr><td>Well-operated property (top 25%)</td><td>7%–11% net annually</td></tr>
                <tr><td>Premium / Aldea Zamá</td><td>9%–14% net annually</td></tr>
              </tbody>
            </table>
          </div>
          <p>Key factors that drive returns above market average:</p>
          <ul>
            <li>Professional property management with dynamic pricing</li>
            <li>High-quality photography and listing optimization</li>
            <li>Location within proven rental demand zones</li>
            <li>Amenities (pool, gym, coworking) that justify higher nightly rates</li>
          </ul>

          <h2>Common Mistakes Foreign Buyers Make in Tulum</h2>

          <p><strong>Buying ejido land</strong></p>
          <p>The single most common — and costly — mistake. Ejido is communal agricultural land that cannot be legally transferred through normal real estate channels. Always verify title origin before signing anything.</p>

          <p><strong>Skipping due diligence on the developer</strong></p>
          <p>In a fast-growing market, not all developers deliver. Check their track record: completed projects, delivery timelines, and financial structure of the development.</p>

          <p><strong>Trusting projected occupancy rates</strong></p>
          <p>Developers often project 80%–90% annual occupancy. The actual market average is 44%–48%. A well-operated property can reach 55%–65% — but it requires active management, not passive ownership.</p>

          <p><strong>Ignoring HOA restrictions</strong></p>
          <p>Some condo buildings prohibit or restrict short-term rentals. If Airbnb income is part of your investment thesis, confirm rental allowances in writing before purchasing.</p>

          <p><strong>Not budgeting for closing costs</strong></p>
          <p>Many first-time buyers in Mexico are surprised by the 6%–9% closing cost. Factor this into your investment model from the start.</p>

          <h2>Do I Need to Be in Mexico to Buy?</h2>
          <p>No. Many foreign buyers complete the entire transaction remotely through:</p>
          <ul>
            <li><strong>Power of attorney</strong> (<em>poder notarial</em>) — allows your Mexican notary or legal representative to sign on your behalf</li>
            <li><strong>Wire transfers</strong> — international wire transfers are the standard payment method</li>
            <li><strong>Digital document signing</strong> — for the purchase agreement and supporting documents</li>
          </ul>
          <p>Your broker and notary can guide you through the remote closing process.</p>

          <h2>Does Buying Property Give Me Mexican Residency?</h2>
          <p>As of 2026, purchasing property in Mexico <strong>does not automatically grant residency or citizenship</strong>. Mexico does not have a formal golden visa program tied to real estate.</p>
          <p>However, many foreign property owners obtain <strong>Temporary Residency by economic solvency</strong> — a pathway that requires demonstrating sufficient income or assets, which a Mexican property can help support as part of your financial profile.</p>

          <h2>Why Tulum — And Why Now?</h2>
          <p>The fundamentals remain strong:</p>
          <ul>
            <li><strong>New international airport</strong> (2024) — direct connections to the US, Canada, and Europe</li>
            <li><strong>Maya Train</strong> — Tulum to Cancún in under 2 hours</li>
            <li><strong>Growing luxury tourism</strong> — Tulum's brand as a high-end destination continues to strengthen globally</li>
            <li><strong>USD-denominated market</strong> — properties are priced in USD, providing a natural hedge for North American and European buyers</li>
            <li><strong>Strong rental demand</strong> — 13,000+ active Airbnb listings with consistent year-round demand</li>
          </ul>
          <p>The main risk: supply has grown faster than demand in some segments. Selectivity matters more in 2026 than it did in 2020. The right development, in the right zone, with the right operator makes the difference between a performing and an underperforming asset.</p>
        </div>

        <div className="mt-16 bg-[#0A2235] rounded-[20px] p-8 md:p-12 text-center">
          <h2 className="font-playfair text-2xl md:text-3xl text-[#FAFAF7] mb-4">
            {es ? '¿Listo para invertir? Tu siguiente paso' : 'Ready to Invest? Here\'s Your Next Step'}
          </h2>
          <p className="font-inter text-[#FAFAF7]/70 mb-8 max-w-xl mx-auto leading-relaxed">
            {es ? 'En Marea Riviera nos especializamos en ayudar a inversionistas nacionales e internacionales a encontrar la propiedad correcta en Tulum, Playa del Carmen y Puerto Morelos.' : 'At Marea Riviera, we specialize in helping national and international investors find the right property in Tulum, Playa del Carmen, and Puerto Morelos. We handle due diligence, developer vetting, fideicomiso coordination, and rental operator selection — so you invest with confidence from wherever you are in the world.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-[#2BBFBF] hover:bg-[#7ECFCF] text-[#0A2235] font-medium rounded-full px-8 py-6 text-base" data-testid="blog-cta-whatsapp">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">{es ? 'Agendar consulta gratuita' : 'Schedule a free consultation'}</a>
            </Button>
            <Button onClick={scrollToContact} variant="outline" className="border-[#2BBFBF] text-[#2BBFBF] hover:bg-[#2BBFBF] hover:text-[#0A2235] rounded-full px-8 py-6 text-base" data-testid="blog-cta-contact">
              {es ? 'Ir al formulario de contacto' : 'Go to contact form'}
            </Button>
          </div>
          <p className="font-inter text-[#FAFAF7]/40 text-sm mt-6">{es ? 'Sin compromiso. Respuesta en menos de 2 horas.' : 'No commitment. Response within 2 hours.'}</p>
        </div>

        <div className="mt-12 pt-8 border-t border-[#0A2235]/10">
          <p className="font-inter text-[#0A2235]/50 text-sm italic leading-relaxed">
            Data sources: TheLatinvestor, Tribu Tulum, Maya Ocean, Mexico's Constitution Article 27, SRE fideicomiso guidelines, AirDNA and Airbtics 2025–2026 market data. Marea Riviera is a real estate investment brokerage specializing in Tulum, Playa del Carmen, and Puerto Morelos.
          </p>
        </div>
      </article>
    </div>
  );
}
